import type { EvidenceCapsuleRef } from "@cogo/schema/productization-spine";
import {
  deriveStudioCandidateLifecycleProjections,
  type StudioOrchestrationRunReadback,
} from "@/features/chat/services/studio-orchestration-journal-api";

export const STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION =
  "cogo.studio-candidate-lifecycle-descriptor.v2" as const;

export const CANDIDATE_VERIFICATION_ROLES = [
  "sufficiency-assessment",
  "validator-safety-pass",
  "second-model-judge",
  "render-execution-receipt",
  "verification-capsule",
] as const;

export type CandidateVerificationRole =
  (typeof CANDIDATE_VERIFICATION_ROLES)[number];

export type CandidateVerificationAuthority =
  | "source-assessment-authority"
  | "validator-runtime"
  | "independent-model-judge"
  | "renderer-runtime"
  | "publishing-kernel";

export const CANDIDATE_VERIFICATION_AUTHORITIES: Record<
  CandidateVerificationRole,
  CandidateVerificationAuthority
> = {
  "sufficiency-assessment": "source-assessment-authority",
  "validator-safety-pass": "validator-runtime",
  "second-model-judge": "independent-model-judge",
  "render-execution-receipt": "renderer-runtime",
  "verification-capsule": "publishing-kernel",
};

export interface StudioCandidateLifecycleDescriptor {
  schemaVersion: typeof STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION;
  projectId: string;
  skuId: string;
  candidateId: string;
  journalRunId: string;
  candidateRevision: string;
  assessmentRevision: string | null;
}

export type CandidateVerificationOverallStatus =
  | "pending"
  | "checking"
  | "verified"
  | "blocked"
  | "unavailable";

export type CandidateVerificationRoleStatus =
  | "pending"
  | "checking"
  | "ready"
  | "verified"
  | "blocked"
  | "unavailable";

export type CandidateVerificationCause =
  | "journal-checking"
  | "journal-unavailable"
  | "journal-scope-invalid"
  | "journal-contract-invalid"
  | "verification-pending"
  | "producer-receipt-pending"
  | "verification-incomplete"
  | "source-assessment-current"
  | "source-assessment-insufficient"
  | "source-assessment-missing"
  | "source-assessment-stale"
  | "durable-journal-verified";

export interface CandidateVerificationRoleView {
  role: CandidateVerificationRole;
  authority: CandidateVerificationAuthority;
  status: CandidateVerificationRoleStatus;
  cause: CandidateVerificationCause;
  evidenceRef: EvidenceCapsuleRef | null;
}

export interface CandidateVerificationModel {
  overall: CandidateVerificationOverallStatus;
  cause: CandidateVerificationCause;
  roles: CandidateVerificationRoleView[];
  verifiedAt: string | null;
}

export type CandidateVerificationReadbackState =
  | { status: "checking" }
  | { status: "ready"; readback: StudioOrchestrationRunReadback | null }
  | { status: "error" };

const DESCRIPTOR_KEYS = [
  "schemaVersion",
  "projectId",
  "skuId",
  "candidateId",
  "journalRunId",
  "candidateRevision",
  "assessmentRevision",
] as const;
const IDENTIFIER_LIMIT = 200;
const REVISION_LIMIT = 256;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f-\u009f]/;
const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function boundedText(value: unknown, limit: number): value is string {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= limit
  );
}

function boundedIdentifier(value: unknown): value is string {
  return (
    boundedText(value, IDENTIFIER_LIMIT) &&
    !CONTROL_CHARACTER_PATTERN.test(value)
  );
}

export function parseStudioCandidateLifecycleDescriptor(
  value: unknown,
): StudioCandidateLifecycleDescriptor {
  const input = record(value);
  if (!input) throw new Error("candidate_lifecycle_descriptor_invalid");
  if (
    Object.keys(input).length !== DESCRIPTOR_KEYS.length ||
    DESCRIPTOR_KEYS.some((key) => !Object.hasOwn(input, key))
  ) {
    throw new Error("candidate_lifecycle_descriptor_shape_invalid");
  }
  if (
    input.schemaVersion !== STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION ||
    !boundedIdentifier(input.projectId) ||
    !boundedIdentifier(input.skuId) ||
    !boundedIdentifier(input.candidateId) ||
    !boundedIdentifier(input.journalRunId) ||
    !(boundedText(input.candidateRevision, REVISION_LIMIT) &&
      SHA256_PATTERN.test(input.candidateRevision)) ||
    !(
      input.assessmentRevision === null ||
      (boundedText(input.assessmentRevision, REVISION_LIMIT) &&
        SHA256_PATTERN.test(input.assessmentRevision))
    )
  ) {
    throw new Error("candidate_lifecycle_descriptor_contract_invalid");
  }
  return {
    schemaVersion: STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION,
    projectId: input.projectId,
    skuId: input.skuId,
    candidateId: input.candidateId,
    journalRunId: input.journalRunId,
    candidateRevision: input.candidateRevision,
    assessmentRevision: input.assessmentRevision,
  };
}

export function readProjectCandidateLifecycleDescriptor(
  project: unknown,
): StudioCandidateLifecycleDescriptor | null {
  const root = record(project);
  const settings = record(root?.settings);
  const settingsMetadata = record(settings?.cogoApplicationMetadata);
  const metadata = record(root?.metadata);
  for (const candidate of [
    settingsMetadata?.candidateLifecycle,
    metadata?.candidateLifecycle,
  ]) {
    try {
      return parseStudioCandidateLifecycleDescriptor(candidate);
    } catch {
      // Tolerant project read: malformed metadata is never status authority.
    }
  }
  return null;
}

export function withCandidateLifecycleDescriptorSettings(
  existingSettings: unknown,
  descriptor: StudioCandidateLifecycleDescriptor | null | undefined,
): Record<string, unknown> {
  const settings = { ...(record(existingSettings) ?? {}) };
  if (!descriptor) return settings;
  const parsed = parseStudioCandidateLifecycleDescriptor(descriptor);
  settings.cogoApplicationMetadata = {
    ...(record(settings.cogoApplicationMetadata) ?? {}),
    candidateLifecycle: parsed,
  };
  return settings;
}

function sourceAssessmentState(
  descriptor: StudioCandidateLifecycleDescriptor,
  currentAssessmentRevision: string | null | undefined,
  currentAssessmentReadiness:
    | "sufficient"
    | "insufficient"
    | null
    | undefined,
): "current" | "insufficient" | "missing" | "stale" {
  if (!descriptor.assessmentRevision || !currentAssessmentRevision) {
    return "missing";
  }
  if (descriptor.assessmentRevision !== currentAssessmentRevision) {
    return "stale";
  }
  return currentAssessmentReadiness === "sufficient"
    ? "current"
    : "insufficient";
}

function sourceAssessmentBlocker(
  state: "insufficient" | "missing" | "stale",
): CandidateVerificationCause {
  if (state === "stale") return "source-assessment-stale";
  if (state === "insufficient") return "source-assessment-insufficient";
  return "source-assessment-missing";
}

function baseRoles(
  descriptor: StudioCandidateLifecycleDescriptor,
  currentAssessmentRevision: string | null | undefined,
  currentAssessmentReadiness:
    | "sufficient"
    | "insufficient"
    | null
    | undefined,
): CandidateVerificationRoleView[] {
  const assessmentState = sourceAssessmentState(
    descriptor,
    currentAssessmentRevision,
    currentAssessmentReadiness,
  );
  return CANDIDATE_VERIFICATION_ROLES.map((role) => {
    if (role !== "sufficiency-assessment") {
      return {
        role,
        authority: CANDIDATE_VERIFICATION_AUTHORITIES[role],
        status: "pending",
        cause: "producer-receipt-pending",
        evidenceRef: null,
      };
    }
    if (assessmentState === "current") {
      return {
        role,
        authority: CANDIDATE_VERIFICATION_AUTHORITIES[role],
        status: "ready",
        cause: "source-assessment-current",
        evidenceRef: null,
      };
    }
    return {
      role,
      authority: CANDIDATE_VERIFICATION_AUTHORITIES[role],
      status: "blocked",
      cause: sourceAssessmentBlocker(assessmentState),
      evidenceRef: null,
    };
  });
}

function unavailableRoles(): CandidateVerificationRoleView[] {
  return CANDIDATE_VERIFICATION_ROLES.map((role) => ({
    role,
    authority: CANDIDATE_VERIFICATION_AUTHORITIES[role],
    status: "unavailable",
    cause: "journal-unavailable",
    evidenceRef: null,
  }));
}

function finalNodeState(
  readback: StudioOrchestrationRunReadback,
  nodeId: string,
): "pending" | "running" | "completed" | "failed" {
  let state: "pending" | "running" | "completed" | "failed" = "pending";
  for (const event of readback.events) {
    if (event.nodeId !== nodeId) continue;
    if (event.type === "node.started") state = "running";
    if (event.type === "node.completed") state = "completed";
    if (event.type === "node.failed") state = "failed";
  }
  return state;
}

function exactVerificationBindings(
  value: ReadonlyArray<{
    role: string;
    ref: EvidenceCapsuleRef;
  }>,
): value is Array<{
  role: CandidateVerificationRole;
  ref: EvidenceCapsuleRef;
}> {
  if (value.length !== CANDIDATE_VERIFICATION_ROLES.length) return false;
  const roles = new Set(value.map((binding) => binding.role));
  const contentHashes = new Set(
    value.map((binding) => binding.ref.contentHash),
  );
  return (
    roles.size === CANDIDATE_VERIFICATION_ROLES.length &&
    contentHashes.size === CANDIDATE_VERIFICATION_ROLES.length &&
    CANDIDATE_VERIFICATION_ROLES.every((role) => roles.has(role))
  );
}

export function deriveCandidateVerificationModel(input: {
  descriptor: StudioCandidateLifecycleDescriptor;
  currentAssessmentRevision: string | null | undefined;
  currentAssessmentReadiness:
    | "sufficient"
    | "insufficient"
    | null
    | undefined;
  journal: CandidateVerificationReadbackState;
}): CandidateVerificationModel {
  const roles = baseRoles(
    input.descriptor,
    input.currentAssessmentRevision,
    input.currentAssessmentReadiness,
  );
  const assessmentState = sourceAssessmentState(
    input.descriptor,
    input.currentAssessmentRevision,
    input.currentAssessmentReadiness,
  );
  if (input.journal.status === "checking") {
    return {
      overall: "checking",
      cause: "journal-checking",
      roles,
      verifiedAt: null,
    };
  }
  if (input.journal.status === "error" || !input.journal.readback) {
    return {
      overall: "unavailable",
      cause: "journal-unavailable",
      roles: unavailableRoles(),
      verifiedAt: null,
    };
  }

  const { readback } = input.journal;
  if (
    readback.run.projectId !== input.descriptor.projectId ||
    readback.run.runId !== input.descriptor.journalRunId ||
    readback.plan.projectId !== input.descriptor.projectId ||
    readback.plan.runId !== input.descriptor.journalRunId ||
    readback.plan.planId !== readback.run.planId
  ) {
    return {
      overall: "blocked",
      cause: "journal-scope-invalid",
      roles,
      verifiedAt: null,
    };
  }

  let projection;
  try {
    projection = deriveStudioCandidateLifecycleProjections(
      readback.events,
    ).find(
      (candidate) =>
        candidate.projectId === input.descriptor.projectId &&
        candidate.journalRunId === input.descriptor.journalRunId &&
        candidate.skuId === input.descriptor.skuId &&
        candidate.candidateId === input.descriptor.candidateId,
    );
  } catch {
    return {
      overall: "blocked",
      cause: "journal-contract-invalid",
      roles,
      verifiedAt: null,
    };
  }

  if (!projection || projection.state !== "verified") {
    return {
      overall: assessmentState === "current" ? "pending" : "blocked",
      cause:
        assessmentState === "current"
          ? "verification-pending"
          : sourceAssessmentBlocker(assessmentState),
      roles,
      verifiedAt: null,
    };
  }

  const previewTransition = projection.history.find(
    (event) =>
      event.transition === "plan" && event.toState === "preview-candidate",
  );
  if (
    !previewTransition ||
    previewTransition.evidenceRefs.length !== 1 ||
    previewTransition.evidenceRefs[0]?.role !== "preview-plan" ||
    previewTransition.evidenceRefs[0].ref.contentHash !==
      input.descriptor.candidateRevision
  ) {
    return {
      overall: "blocked",
      cause: "journal-contract-invalid",
      roles,
      verifiedAt: null,
    };
  }

  const verifyTransition = [...projection.history]
    .reverse()
    .find((event) => event.transition === "verify");
  const envelope = verifyTransition
    ? readback.events.find(
        (event) => event.eventId === verifyTransition.transitionId,
      )
    : undefined;
  const verifyNode = envelope?.nodeId
    ? readback.plan.nodes.find((node) => node.id === envelope.nodeId)
    : undefined;
  const bindings = verifyTransition?.evidenceRefs ?? [];
  const completedByTrustedNode = Boolean(
    envelope?.type === "node.completed" &&
      envelope.nodeId &&
      verifyNode &&
      ["gate", "readback"].includes(verifyNode.kind) &&
      ["server", "runtime"].includes(verifyNode.policy.authority) &&
      verifyNode.operation?.name === "cogo.productization.verify" &&
      finalNodeState(readback, envelope.nodeId) === "completed",
  );
  const exactBindings = exactVerificationBindings(bindings);
  const sufficiencyRef = exactBindings
    ? bindings.find((binding) => binding.role === "sufficiency-assessment")
        ?.ref
    : null;
  const verified = Boolean(
    assessmentState === "current" &&
      completedByTrustedNode &&
      exactBindings &&
      sufficiencyRef?.contentHash === input.descriptor.assessmentRevision,
  );
  if (!verified) {
    return {
      overall: "blocked",
      cause:
        assessmentState === "current"
          ? "verification-incomplete"
          : sourceAssessmentBlocker(assessmentState),
      roles,
      verifiedAt: null,
    };
  }

  return {
    overall: "verified",
    cause: "durable-journal-verified",
    roles: CANDIDATE_VERIFICATION_ROLES.map((role) => ({
      role,
      authority: CANDIDATE_VERIFICATION_AUTHORITIES[role],
      status: "verified",
      cause: "durable-journal-verified",
      evidenceRef:
        bindings.find((binding) => binding.role === role)?.ref ?? null,
    })),
    verifiedAt: verifyTransition?.occurredAt ?? null,
  };
}
